import React, { useState, useCallback, useEffect } from 'react';
import { Grid, Button, Box, CircularProgress } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { NetworkRequestMultipart } from '../../../../Utilities/NetworkRequests/NetworkRequests';
import { buyerIntegrationState, verticalList } from '../defaultState/state';
import { formatSubmitValues } from '../../../../Utilities/SubmitHelper/FormatForSubmit';
import { validateForSubmit } from '../validation/validation';
import {
  Select,
  TextField,
  TextArea,
  Checkbox,
  FileUploadDragDrop
} from '../../../../FormContainer/FormComponents';
import Dialog from '../../../../Dialog';

export const styles = () => ({
  formWrapper: {
    overflowY: 'auto'
  },
  formContainer: {
    overflowY: 'auto',
    maxWidth: '530px',
    margin: 'auto',
    paddingBottom: '2rem'
  },
  submitButton: {
    color: '#ffffff',
    backgroundColor: '#00adb5'
  }
});

const BuyerIntegration = (props) => {
  const { classes } = props;
  const [formValues, setFormValues] = useState(() => buyerIntegrationState());
  const [submitting, isSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialog, setDialog] = useState({
    message: '',
    type: 'error',
    title: 'Error'
  });

  useEffect(() => {
    if (formValues.is_spec_document.value) {
      setFormValues((prevFormData) => {
        const field = prevFormData.spec_url;
        const newField = { ...field, value: '' };
        const newFormData = {
          ...prevFormData,
          spec_url: newField
        };
        return newFormData;
      });
    }

    if (!formValues.is_spec_document.value) {
      setFormValues((prevFormData) => {
        const field = prevFormData.spec_document;
        const newField = { ...field, value: null };
        const newFormData = {
          ...prevFormData,
          spec_document: newField
        };
        return newFormData;
      });
    }
  }, [formValues.is_spec_document.value]);

  const handleChange = useCallback((name, value) => {
    setDialog({ message: '' });
    setFormValues((prevFormData) => {
      const field = prevFormData[name];
      const newField = { ...field, value };
      const newFormData = {
        ...prevFormData,
        [name]: newField
      };
      return newFormData;
    });
  }, []);

  const toggleDialog = useCallback(
    (message = '', type = 'error', title = 'Error') => {
      setDialog((prevDialog) => ({ ...prevDialog, message, type, title }));
    },
    []
  );

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSubmit = () => {
    const { valid, messages } = validateForSubmit(formValues);
    if (!valid) {
      toggleDialog(messages, 'error');
      setOpenDialog(true);
      return false;
    }
    save();
  };

  const save = async () => {
    isSubmitting(true);
    try {
      const { data } = await NetworkRequestMultipart(
        'buyers',
        formatSubmitValues(formValues),
        ['spec_document'],
        'sendBuyerIntegrationEmail'
      );
      toggleDialog(
        data.message ? data.message : data,
        data.success ? 'success' : 'error',
        data.success ? 'Success' : 'Error'
      );
      setOpenDialog(true);
    } catch (error) {
      isSubmitting(false);
      toggleDialog('An error occurred', 'error');
      setOpenDialog(true);
    } finally {
      isSubmitting(false);
    }
  };

  return (
    <div className={classes.formWrapper}>
      <Grid className={classes.formContainer}>
        <TextField
          label={formValues.account_owner_name.label}
          value={formValues.account_owner_name.value}
          name="account_owner_name"
          onChange={handleChange}
        />
        <TextField
          label={formValues.account_owner_email.label}
          value={formValues.account_owner_email.value}
          name="account_owner_email"
          onChange={handleChange}
        />
        <TextField
          label={formValues.buyer_name.label}
          value={formValues.buyer_name.value}
          name="buyer_name"
          onChange={handleChange}
        />
        <TextField
          label={formValues.client_email.label}
          value={formValues.client_email.value}
          name="client_email"
          onChange={handleChange}
        />
        <TextField
          label={formValues.client_name.label}
          value={formValues.client_name.value}
          name="client_name"
          onChange={handleChange}
        />
        <TextField
          label={formValues.buyer_partners_list_name.label}
          value={formValues.buyer_partners_list_name.value}
          name="buyer_partners_list_name"
          onChange={handleChange}
        />
        <Select
          label="Vertical"
          value={formValues.vertical.value}
          name="vertical"
          onChange={handleChange}
          options={verticalList}
        />
        <Checkbox
          value={formValues.is_spec_document.value}
          name="is_spec_document"
          label={formValues.is_spec_document.label}
          labelInline
          onChange={handleChange}
        />
        {formValues.is_spec_document.value ? (
          <FileUploadDragDrop
            label={formValues.spec_document.label}
            value={formValues.spec_document.value}
            name="spec_document"
            onChange={handleChange}
          />
        ) : (
          <TextField
            label={formValues.spec_url.label}
            value={formValues.spec_url.value}
            name="spec_url"
            onChange={handleChange}
          />
        )}
        <Checkbox
          value={formValues.campaigns_active.value}
          name="campaigns_active"
          label={formValues.campaigns_active.label}
          labelInline
          onChange={handleChange}
        />
        <Checkbox
          value={formValues.contracts_signed.value}
          name="contracts_signed"
          label={formValues.contracts_signed.label}
          labelInline
          onChange={handleChange}
        />
        <Checkbox
          value={formValues.bid_components_done.value}
          name="bid_components_done"
          label={formValues.bid_components_done.label}
          labelInline
          onChange={handleChange}
        />
        <TextArea
          label={formValues.buyer_notes.label}
          value={formValues.buyer_notes.value}
          name="buyer_notes"
          onChange={handleChange}
        />

        <Box mt={2} textAlign="center">
          <Button
            className={classes.submitButton}
            id="submitButton"
            variant="contained"
            color="inherit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <CircularProgress
                sx={{
                  color: '#ffffff'
                }}
                size={24}
              />
            ) : (
              'Submit'
            )}
          </Button>
        </Box>
      </Grid>
      <Dialog
        open={openDialog}
        title={dialog.title}
        type={dialog.type}
        confirmButtonText="Close"
        onRequestClose={handleDialogClose}
      >
        {dialog.message}
      </Dialog>
    </div>
  );
};

export default withStyles(BuyerIntegration, styles);
